import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { adminListPayoutsByUser } from "@/lib/wallet.functions";

export const Route = createFileRoute("/admin/payouts/users")({
  head: () => ({ meta: [{ title: "Admin · Payouts por usuario — ZYNTRA" }] }),
  component: PayoutsByUserPage,
});

async function withAuth<T>(fn: (h: any) => Promise<T>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return fn({ authorization: token ? `Bearer ${token}` : "" });
}

function fmt(n: number) {
  return `CRC ${Number(n || 0).toLocaleString("es-CR", { maximumFractionDigits: 0 })}`;
}

function PayoutsByUserPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate({ to: "/login" }); return; }
    (async () => {
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle();
      const admin = !!data;
      setIsAdmin(admin);
      if (admin) {
        try {
          const { rows } = await withAuth((h) => adminListPayoutsByUser({ headers: h } as any));
          setRows(rows);
        } catch (e) { console.error(e); }
      }
      setLoading(false);
    })();
  }, [user, authLoading]);

  if (authLoading || loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 pt-32 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Acceso restringido</h1>
      </div>
    );
  }

  const totalToPay = rows.reduce((s, r) => s + Number(r.total_pending_amount || 0), 0);

  return (
    <div className="mx-auto max-w-6xl px-4 pb-20 pt-24 sm:px-6">
      <Link to="/admin/payouts" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Lista de solicitudes
      </Link>

      <h1 className="text-2xl font-black text-foreground sm:text-3xl">Pagos a ejecutar por usuario</h1>
      <p className="mt-1 text-sm text-muted-foreground mb-6">Total a pagar: <strong className="gradient-text">{fmt(totalToPay)}</strong> · {rows.length} usuario(s)</p>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">Usuario</th>
                <th className="px-4 py-3 text-left">Total a pagar</th>
                <th className="px-4 py-3 text-left">Solicitudes</th>
                <th className="px-4 py-3 text-left">Método</th>
                <th className="px-4 py-3 text-left">Cuenta</th>
                <th className="px-4 py-3 text-left">Más antigua</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No hay payouts pendientes</td></tr>
              )}
              {rows.map((r) => {
                const ms = r.payout_account || {};
                return (
                  <tr key={r.user_id} className="border-t border-border hover:bg-accent/30">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{r.display_name || r.email}</p>
                      <p className="text-xs text-muted-foreground">{r.email}</p>
                    </td>
                    <td className="px-4 py-3 font-bold text-primary">{fmt(r.total_pending_amount)}</td>
                    <td className="px-4 py-3 text-xs text-foreground">{r.request_count}</td>
                    <td className="px-4 py-3 text-xs text-foreground">{r.payout_method || "—"}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {ms.sinpe_number && <p>SINPE: {ms.sinpe_number}</p>}
                      {ms.bank_name && <p>{ms.bank_name}</p>}
                      {ms.iban && <p className="font-mono">{ms.iban}</p>}
                      {ms.account_number && <p>{ms.account_number}</p>}
                      {ms.email_for_payment && <p>{ms.email_for_payment}</p>}
                      {ms.full_name && <p className="text-foreground">{ms.full_name}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{r.oldest_request_at ? new Date(r.oldest_request_at).toLocaleString("es-CR") : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
